import type { StoryType } from "@/config";
import { track } from "@/features/analytics";
import { resolveDesignSystem } from "@/pages/editor/modules/design-system/modules/theme/utils/resolve-design-system";
import { useGetThemesQuery } from "@/shared/services";
import { pagesKeys } from "@/shared/services/pages/pages.keys";
import type { TPage } from "@/shared/services/pages/types";
import { buildTemplateStoryFromSlides } from "@/shared/utils/import-story-from-json";
import { useCreateStory } from "@providers/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFileToStorydocStore } from "../stores";
import {
  composeChosenSlides,
  selectChangedCount,
} from "../stores/file-to-storydoc-generation.store";
import {
  findImportByStore,
  useImportRegistry,
} from "../stores/import-registry.store";
import { useImportStoreApi } from "../stores/import-store-context";
import { buildImportedFrom } from "../utils/build-imported-from";
import { closeImportDialog } from "../utils/import-dialog-id";
import { trackImport } from "../utils/track-import";

export const useAcceptImport = () => {
  const store = useImportStoreApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const handleCreateStory = useCreateStory();
  const { data: themes } = useGetThemesQuery({ enabled: true });
  const [isAccepting, setIsAccepting] = useState(false);

  const accept = useCallback(async () => {
    const s = store.getState();
    if (s.rows.length === 0 || isAccepting) return;
    setIsAccepting(true);
    const slides = composeChosenSlides(s.rows);
    const templateStory = buildTemplateStoryFromSlides({ slides });
    const resolved = resolveDesignSystem({
      base: templateStory.settings.designSystemV2,
      selectedDesignSystemId: s.selectedDesignSystemId,
      themes,
      pdfDesignColors: s.designColors,
    });
    const settings = templateStory.settings as typeof templateStory.settings & {
      customColors?: string[];
    };
    settings.designSystemV2 =
      resolved.designSystemV2 as typeof templateStory.settings.designSystemV2;
    if (resolved.customColors) settings.customColors = resolved.customColors;

    const storyTitle = s.title;
    let created: Awaited<ReturnType<typeof handleCreateStory>>;
    try {
      created = await handleCreateStory({
        status: "Draft",
        title: storyTitle,
        template: templateStory as unknown as StoryType,
        type: "create",
        forceUseTemplateSlides: true,
        keepTemplateDesignSystem: true,
        skipToast: true,
        importedFrom: buildImportedFrom(s),
      });
    } catch {
      trackImport("error", { stage: "accept" }, store);
      setIsAccepting(false);
      return; // useCreateStory already toasted
    }
    if (!created) {
      trackImport("error", { stage: "accept" }, store);
      setIsAccepting(false);
      return;
    }
    const meta = created as {
      previewUrl?: string;
      settings?: { contentPointer?: string };
    };
    store.getState().setCreatedStory({
      _id: created._id,
      previewUrl: meta.previewUrl,
      contentPointer: meta.settings?.contentPointer,
    });
    track("success file to storydoc generation");
    trackImport(
      "accept",
      {
        storyId: created._id,
        changed: selectChangedCount(s.rows),
        static: s.rows.filter((r) => r.asStatic).length,
        regenerated: s.rows.filter((r) => r.setRef.kind === "regeneration")
          .length,
        durationMs: s.startedAt
          ? Date.now() - new Date(s.startedAt).getTime()
          : null,
      },
      store,
    );
    void queryClient.invalidateQueries({ queryKey: pagesKeys.imported() });

    // The stories-list search endpoint is briefly eventually-consistent; patch
    // the cached list with the pointer we hold and re-pull shortly after.
    queryClient.setQueriesData<TPage[] | undefined>(
      { queryKey: pagesKeys.lists() },
      (old) =>
        old?.map((p) =>
          p._id === created._id
            ? ({
                ...p,
                previewUrl: meta.previewUrl ?? p.previewUrl,
                settings: {
                  ...p.settings,
                  contentPointer:
                    meta.settings?.contentPointer ?? p.settings?.contentPointer,
                },
              } as TPage)
            : p,
        ),
    );
    for (const delay of [3000, 8000, 15000]) {
      window.setTimeout(
        () => queryClient.invalidateQueries({ queryKey: pagesKeys.lists() }),
        delay,
      );
    }

    const entry = findImportByStore(store);
    closeImportDialog();
    useFileToStorydocStore.getState().reset();
    if (entry) useImportRegistry.getState().removeImport(entry.id);
    else store.getState().reset();
    setIsAccepting(false);
    navigate(`/pages/editor/${created._id}`);
  }, [handleCreateStory, isAccepting, navigate, queryClient, store, themes]);

  return { accept, isAccepting };
};
